import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import StarIcon from '@mui/icons-material/Star'
import ArchiveIcon from '@mui/icons-material/Archive'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FiberNewIcon from '@mui/icons-material/FiberNew'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import SEO from '../utils/seo'
import { colors, textFieldSx as themeTextFieldSx, selectSx as themeSelectSx, dialogPaperSx } from '../styles/theme'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const CATEGORIES = [
  'Gen AI',
  'App Development',
  'Developer Experience',
  'Data',
  'Teams & Culture',
  'Industry',
  'Engineering Dilemmas',
  'System Design',
  'Philosophy'
]

const STATUSES = ['new', 'shortlisted', 'used', 'archived']

const STATUS_CONFIG = {
  new: { label: 'New', color: 'var(--color-accent-info)', icon: <FiberNewIcon fontSize="small" /> },
  shortlisted: { label: 'Shortlisted', color: 'var(--color-accent-warning)', icon: <StarIcon fontSize="small" /> },
  used: { label: 'Used', color: 'var(--color-accent-primary)', icon: <CheckCircleIcon fontSize="small" /> },
  archived: { label: 'Archived', color: 'var(--color-text-muted)', icon: <ArchiveIcon fontSize="small" /> }
}

const textFieldSx = themeTextFieldSx
const selectSx = themeSelectSx

const PromptIdeas = () => {
  const [ideas, setIdeas] = useState([])
  const [filterCategory, setFilterCategory] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({ category: '', prompt: '', notes: '' })
  const [editing, setEditing] = useState(null)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null })
  const [expandedId, setExpandedId] = useState(null)
  const [editingTalkTrack, setEditingTalkTrack] = useState(null)
  const [talkTrackDraft, setTalkTrackDraft] = useState([])
  const [newBullet, setNewBullet] = useState('')
  const [copiedId, setCopiedId] = useState(null)

  useEffect(() => {
    fetchIdeas()
  }, [filterCategory, filterStatus])

  const fetchIdeas = async () => {
    try {
      const params = new URLSearchParams()
      if (filterCategory) params.set('category', filterCategory)
      if (filterStatus) params.set('status', filterStatus)
      const qs = params.toString()
      const response = await fetch(`${API_URL}/prompt-ideas${qs ? '?' + qs : ''}`)
      const data = await response.json()
      setIdeas(data)
    } catch (error) {
      console.error('Error fetching prompt ideas:', error)
      setMessage({ type: 'error', text: 'Failed to fetch prompt ideas' })
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      const url = editing
        ? `${API_URL}/prompt-ideas/${editing}`
        : `${API_URL}/prompt-ideas`

      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        setMessage({
          type: 'success',
          text: editing ? 'Prompt idea updated!' : 'Prompt idea created!'
        })
        resetForm()
        fetchIdeas()
      } else {
        throw new Error('Failed to save')
      }
    } catch (error) {
      console.error('Error saving prompt idea:', error)
      setMessage({ type: 'error', text: 'Failed to save prompt idea' })
    }
  }

  const resetForm = () => {
    setFormData({ category: '', prompt: '', notes: '' })
    setEditing(null)
    setShowForm(false)
  }

  const handleEdit = (idea) => {
    setFormData({
      category: idea.category,
      prompt: idea.prompt,
      notes: idea.notes || ''
    })
    setEditing(idea.id)
    setShowForm(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/prompt-ideas/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      })

      if (response.ok) {
        fetchIdeas()
      }
    } catch (error) {
      console.error('Error updating status:', error)
    }
  }

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/prompt-ideas/${deleteDialog.id}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        setMessage({ type: 'success', text: 'Prompt idea deleted!' })
        fetchIdeas()
      } else {
        throw new Error('Failed to delete')
      }
    } catch (error) {
      console.error('Error deleting prompt idea:', error)
      setMessage({ type: 'error', text: 'Failed to delete prompt idea' })
    } finally {
      setDeleteDialog({ open: false, id: null })
    }
  }

  // ── Talk Track helpers ──

  const startEditingTalkTrack = (idea) => {
    setEditingTalkTrack(idea.id)
    setTalkTrackDraft([...(idea.talkTrack || [])])
    setNewBullet('')
  }

  const cancelEditingTalkTrack = () => {
    setEditingTalkTrack(null)
    setTalkTrackDraft([])
    setNewBullet('')
  }

  const addBullet = () => {
    if (!newBullet.trim()) return
    setTalkTrackDraft(prev => [...prev, newBullet.trim()])
    setNewBullet('')
  }

  const removeBullet = (index) => {
    setTalkTrackDraft(prev => prev.filter((_, i) => i !== index))
  }

  const moveBullet = (index, direction) => {
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= talkTrackDraft.length) return
    const updated = [...talkTrackDraft]
    ;[updated[index], updated[newIndex]] = [updated[newIndex], updated[index]]
    setTalkTrackDraft(updated)
  }

  const saveAndCloseTalkTrack = async (ideaId) => {
    try {
      await fetch(`${API_URL}/prompt-ideas/${ideaId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ talkTrack: talkTrackDraft })
      })
      fetchIdeas()
    } catch (error) {
      console.error('Error saving talk track:', error)
      setMessage({ type: 'error', text: 'Failed to save talk track' })
    }
    cancelEditingTalkTrack()
  }

  const copyForTeleprompter = (idea) => {
    const lines = [
      `[PROMPT]`,
      idea.prompt,
      '',
    ]
    if (idea.notes) {
      lines.push(`[NOTES]`, idea.notes, '')
    }
    if (idea.talkTrack?.length > 0) {
      lines.push(`[TALK TRACK]`)
      idea.talkTrack.forEach((point, i) => {
        lines.push(`${i + 1}. ${point}`)
      })
    }
    navigator.clipboard.writeText(lines.join('\n'))
    setCopiedId(idea.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const counts = ideas.reduce((acc, idea) => {
    acc[idea.status] = (acc[idea.status] || 0) + 1
    return acc
  }, {})

  return (
    <>
      <SEO title="Prompt Ideas" />

      <div className="section">
        <div className="container-custom max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-off-white">
                  Prompt Ideas
                </h1>
                <p className="text-gray-400 mt-2 font-mono text-sm">
                  {ideas.length} ideas {filterCategory && `in ${filterCategory}`} {filterStatus && `(${filterStatus})`}
                </p>
              </div>
              <Button
                variant="contained"
                startIcon={showForm ? <CloseIcon /> : <AddIcon />}
                onClick={() => {
                  if (showForm) resetForm()
                  else setShowForm(true)
                }}
                sx={{
                  backgroundColor: showForm ? colors.textMuted : colors.accent,
                  color: colors.textOnAccent,
                  fontWeight: 700,
                  '&:hover': {
                    backgroundColor: showForm ? colors.textFaint : colors.accentHover,
                  },
                }}
              >
                {showForm ? 'Cancel' : 'New Idea'}
              </Button>
            </div>

            {message.text && (
              <Alert
                severity={message.type}
                onClose={() => setMessage({ type: '', text: '' })}
                sx={{ mb: 4 }}
              >
                {message.text}
              </Alert>
            )}

            {/* Add/Edit Form */}
            <AnimatePresence>
              {showForm && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card sx={{ backgroundColor: colors.bgCard, mb: 4 }}>
                    <CardContent sx={{ p: 4 }}>
                      <h2 className="text-2xl font-bold text-off-white mb-6">
                        {editing ? 'Edit Prompt Idea' : 'Add New Prompt Idea'}
                      </h2>

                      <form onSubmit={handleSubmit} className="space-y-4">
                        <FormControl fullWidth>
                          <InputLabel sx={{ color: colors.textMuted }}>Category</InputLabel>
                          <Select
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            required
                            label="Category"
                            sx={selectSx}
                          >
                            {CATEGORIES.map(cat => (
                              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          fullWidth
                          required
                          label="Prompt / Topic Question"
                          name="prompt"
                          multiline
                          rows={3}
                          value={formData.prompt}
                          onChange={handleChange}
                          placeholder='e.g. "Is Kubernetes worth it for a team under 50 engineers?"'
                          sx={textFieldSx}
                        />

                        <TextField
                          fullWidth
                          label="Notes (optional)"
                          name="notes"
                          multiline
                          rows={2}
                          value={formData.notes}
                          onChange={handleChange}
                          placeholder="Talking points, potential guests, angle..."
                          sx={textFieldSx}
                        />

                        <div className="flex gap-4">
                          <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            sx={{
                              backgroundColor: colors.accent,
                              color: colors.textOnAccent,
                              fontWeight: 700,
                              '&:hover': { backgroundColor: colors.accentHover },
                            }}
                          >
                            {editing ? 'Update' : 'Add Idea'}
                          </Button>

                          {editing && (
                            <Button
                              variant="outlined"
                              size="large"
                              onClick={resetForm}
                              sx={{
                                borderColor: colors.secondaryLight,
                                color: colors.secondaryLight,
                                '&:hover': {
                                  borderColor: colors.secondaryLight,
                                  backgroundColor: 'rgba(var(--color-accent-secondary-rgb), 0.1)',
                                },
                              }}
                            >
                              Cancel Edit
                            </Button>
                          )}
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Filters */}
            <div className="mb-6 space-y-3">
              {/* Category filters */}
              <div className="flex flex-wrap gap-2">
                <Chip
                  label="All Categories"
                  onClick={() => setFilterCategory('')}
                  variant={filterCategory === '' ? 'filled' : 'outlined'}
                  sx={{
                    backgroundColor: filterCategory === '' ? colors.secondaryLight : 'transparent',
                    color: filterCategory === '' ? colors.textOnAccent : colors.secondaryLight,
                    borderColor: colors.secondaryLight,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: filterCategory === '' ? colors.secondaryLight : 'rgba(var(--color-accent-secondary-rgb), 0.1)' },
                  }}
                />
                {CATEGORIES.map(cat => (
                  <Chip
                    key={cat}
                    label={cat}
                    onClick={() => setFilterCategory(filterCategory === cat ? '' : cat)}
                    variant={filterCategory === cat ? 'filled' : 'outlined'}
                    sx={{
                      backgroundColor: filterCategory === cat ? colors.secondaryLight : 'transparent',
                      color: filterCategory === cat ? colors.textOnAccent : colors.secondaryLight,
                      borderColor: 'rgba(var(--color-accent-secondary-rgb), 0.3)',
                      '&:hover': { backgroundColor: filterCategory === cat ? colors.secondaryLight : 'rgba(var(--color-accent-secondary-rgb), 0.1)' },
                    }}
                  />
                ))}
              </div>

              {/* Status filters */}
              <div className="flex flex-wrap gap-2">
                <Chip
                  label={`All (${ideas.length})`}
                  onClick={() => setFilterStatus('')}
                  variant={filterStatus === '' ? 'filled' : 'outlined'}
                  sx={{
                    backgroundColor: filterStatus === '' ? colors.info : 'transparent',
                    color: filterStatus === '' ? colors.textOnAccent : colors.info,
                    borderColor: colors.info,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: filterStatus === '' ? colors.info : 'rgba(var(--color-accent-info-rgb), 0.1)' },
                  }}
                />
                {STATUSES.map(status => (
                  <Chip
                    key={status}
                    icon={STATUS_CONFIG[status].icon}
                    label={`${STATUS_CONFIG[status].label} (${counts[status] || 0})`}
                    onClick={() => setFilterStatus(filterStatus === status ? '' : status)}
                    variant={filterStatus === status ? 'filled' : 'outlined'}
                    sx={{
                      backgroundColor: filterStatus === status ? STATUS_CONFIG[status].color : 'transparent',
                      color: filterStatus === status ? colors.textOnAccent : STATUS_CONFIG[status].color,
                      borderColor: STATUS_CONFIG[status].color,
                      '& .MuiChip-icon': { color: filterStatus === status ? colors.textOnAccent : STATUS_CONFIG[status].color },
                      '&:hover': { backgroundColor: filterStatus === status ? STATUS_CONFIG[status].color : 'transparent' },
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Ideas List */}
            <div className="space-y-3">
              <AnimatePresence>
                {ideas.map((idea, index) => (
                  <motion.div
                    key={idea.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3, delay: index * 0.02 }}
                  >
                    <Card sx={{ backgroundColor: colors.bgCard, '&:hover': { backgroundColor: colors.bgElevated }, transition: 'background-color 0.2s' }}>
                      <CardContent sx={{ p: 3 }}>
                        <div className="flex justify-between items-start gap-4">
                          <div
                            className="flex-grow cursor-pointer"
                            onClick={() => setExpandedId(expandedId === idea.id ? null : idea.id)}
                          >
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Chip
                                label={idea.category}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(var(--color-accent-secondary-rgb), 0.15)',
                                  color: colors.secondaryLight,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                }}
                              />
                              <Chip
                                icon={STATUS_CONFIG[idea.status]?.icon}
                                label={STATUS_CONFIG[idea.status]?.label || idea.status}
                                size="small"
                                sx={{
                                  backgroundColor: STATUS_CONFIG[idea.status]?.color ? `color-mix(in srgb, ${STATUS_CONFIG[idea.status].color} 12%, transparent)` : 'rgba(148,163,184,0.12)',
                                  color: STATUS_CONFIG[idea.status]?.color || colors.textMuted,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  '& .MuiChip-icon': { color: STATUS_CONFIG[idea.status]?.color || colors.textMuted },
                                }}
                              />
                            </div>

                            <p className="text-off-white font-medium text-base leading-relaxed">
                              {idea.prompt}
                            </p>

                            {/* Expanded section */}
                            <AnimatePresence>
                              {expandedId === idea.id && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="mt-3 pt-3 border-t border-gray-700"
                                >
                                  {idea.notes && (
                                    <p className="text-gray-400 text-sm mb-3">
                                      <span className="text-gray-500 font-mono text-xs">Notes: </span>
                                      {idea.notes}
                                    </p>
                                  )}

                                  {/* Talk Track Section */}
                                  <div className="mt-3 pt-3 border-t border-gray-700/50">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-gray-500 font-mono text-xs">Talk Track</span>
                                      <div className="flex gap-1">
                                        {(idea.talkTrack?.length > 0) && (
                                          <Button
                                            size="small"
                                            startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              copyForTeleprompter(idea)
                                            }}
                                            sx={{
                                              color: copiedId === idea.id ? colors.accent : colors.info,
                                              fontSize: '0.7rem',
                                              textTransform: 'none',
                                            }}
                                          >
                                            {copiedId === idea.id ? 'Copied!' : 'Copy for Teleprompter'}
                                          </Button>
                                        )}
                                        {editingTalkTrack !== idea.id && (
                                          <Button
                                            size="small"
                                            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              startEditingTalkTrack(idea)
                                            }}
                                            sx={{
                                              color: colors.textMuted,
                                              fontSize: '0.7rem',
                                              textTransform: 'none',
                                            }}
                                          >
                                            {idea.talkTrack?.length ? 'Edit' : 'Add Points'}
                                          </Button>
                                        )}
                                      </div>
                                    </div>

                                    {/* Read-only bullet list */}
                                    {editingTalkTrack !== idea.id && idea.talkTrack?.length > 0 && (
                                      <ol className="space-y-1.5 pl-1">
                                        {idea.talkTrack.map((point, i) => (
                                          <li key={i} className="flex gap-2 text-sm leading-relaxed">
                                            <span className="text-cyan font-mono font-bold shrink-0">{i + 1}.</span>
                                            <span className="text-gray-300">{point}</span>
                                          </li>
                                        ))}
                                      </ol>
                                    )}

                                    {editingTalkTrack !== idea.id && (!idea.talkTrack || idea.talkTrack.length === 0) && (
                                      <p className="text-gray-600 text-xs italic">No talk track points yet.</p>
                                    )}

                                    {/* Inline editor */}
                                    {editingTalkTrack === idea.id && (
                                      <div onClick={(e) => e.stopPropagation()} className="space-y-2">
                                        {talkTrackDraft.map((point, i) => (
                                          <div key={i} className="flex items-center gap-2 group">
                                            <span className="text-cyan font-mono text-xs w-5 text-right shrink-0 font-bold">{i + 1}.</span>
                                            <span className="text-gray-300 text-sm flex-grow">{point}</span>
                                            <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                              <IconButton size="small" onClick={() => moveBullet(i, -1)} disabled={i === 0}
                                                sx={{ color: colors.textMuted, p: '2px' }}>
                                                <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                              <IconButton size="small" onClick={() => moveBullet(i, 1)} disabled={i === talkTrackDraft.length - 1}
                                                sx={{ color: colors.textMuted, p: '2px' }}>
                                                <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                              <IconButton size="small" onClick={() => removeBullet(i)}
                                                sx={{ color: colors.error, p: '2px' }}>
                                                <CloseIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                            </div>
                                          </div>
                                        ))}

                                        <div className="flex gap-2 mt-2">
                                          <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Add a talking point..."
                                            value={newBullet}
                                            onChange={(e) => setNewBullet(e.target.value)}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') { e.preventDefault(); addBullet() }
                                            }}
                                            sx={textFieldSx}
                                          />
                                          <Button size="small" onClick={addBullet} disabled={!newBullet.trim()}
                                            sx={{ color: colors.accent, minWidth: 'auto' }}>
                                            <AddIcon />
                                          </Button>
                                        </div>

                                        <div className="flex gap-2 mt-2">
                                          <Button size="small" variant="contained"
                                            onClick={() => saveAndCloseTalkTrack(idea.id)}
                                            sx={{ backgroundColor: colors.accent, color: colors.textOnAccent, fontWeight: 700,
                                              '&:hover': { backgroundColor: colors.accentHover } }}>
                                            Save
                                          </Button>
                                          <Button size="small" onClick={cancelEditingTalkTrack}
                                            sx={{ color: colors.textMuted }}>
                                            Cancel
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </div>

                                  {/* Status toggle buttons */}
                                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-700/50">
                                    {STATUSES.filter(s => s !== idea.status).map(status => (
                                      <Button
                                        key={status}
                                        size="small"
                                        variant="outlined"
                                        startIcon={STATUS_CONFIG[status].icon}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleStatusChange(idea.id, status)
                                        }}
                                        sx={{
                                          borderColor: `${STATUS_CONFIG[status].color}50`,
                                          color: STATUS_CONFIG[status].color,
                                          fontSize: '0.75rem',
                                          textTransform: 'none',
                                          '&:hover': {
                                            borderColor: STATUS_CONFIG[status].color,
                                            backgroundColor: `${STATUS_CONFIG[status].color}15`,
                                          },
                                        }}
                                      >
                                        {STATUS_CONFIG[status].label}
                                      </Button>
                                    ))}
                                  </div>

                                  <p className="text-gray-600 text-xs mt-3 font-mono">
                                    Added {new Date(idea.createdAt).toLocaleDateString()}
                                  </p>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>

                          {/* Action buttons */}
                          <div className="flex gap-1 shrink-0">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(idea)}
                              sx={{ color: colors.info }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => setDeleteDialog({ open: true, id: idea.id })}
                              sx={{ color: colors.error }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>

              {ideas.length === 0 && (
                <Card sx={{ backgroundColor: colors.bgCard }}>
                  <CardContent sx={{ p: 6, textAlign: 'center' }}>
                    <p className="text-gray-400 text-lg">
                      No prompt ideas yet. Add your first one!
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle>Delete Prompt Idea?</DialogTitle>
        <DialogContent>
          <p className="text-gray-300">
            Are you sure you want to delete this prompt idea? This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, id: null })}
            sx={{ color: colors.textMuted }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            sx={{ color: colors.error, fontWeight: 700 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PromptIdeas
